import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { CvTemplate } from 'domain/template/cv/cv-template';
import { CvTemplateI18n } from 'domain/template/cv/cv-template-i18n';

import { AppLocator } from '../../../../app.locator';

export interface CvTemplateI18nDocument {
	readonly name: string;
	readonly locale: string;
}

export interface CvTemplateDocument {
	readonly id: string;
	readonly organizationId: string;
	readonly updateTimestamp: number;
	readonly creationTimestamp: number;
	readonly localizations: CvTemplateI18nDocument[];
}

export type CvTemplateState = EntityState<CvTemplateDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'cvTemplates' })
export class CvTemplateStore extends EntityStore<CvTemplateState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public asDocument(document: CvTemplate): CvTemplateDocument {
		return document
			? {
					id: document.id,
					organizationId: document.organizationId,
					updateTimestamp: document.updateTimestamp,
					creationTimestamp: document.creationTimestamp,
					localizations: document.localizations.map((loc) => ({
						locale: loc.locale,
						name: loc.name,
					})),
			  }
			: null;
	}

	public asEntity(document: CvTemplateDocument): CvTemplate {
		const localizations = !!document
			? document.localizations.map(
					(loc) => new CvTemplateI18n(loc.name, loc.locale)
			  )
			: [];

		return document
			? new CvTemplate(
					document.id,
					document.organizationId,
					document.updateTimestamp,
					document.creationTimestamp,
					localizations,
					this._observeLocalization(localizations)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
